import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Settings,
  User,
  Bell,
  Palette,
  Shield,
  Database,
  Link,
  Sun,
  Moon,
  Monitor,
  Save,
  Loader2,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuthStore, useThemeStore, useEntityStore } from '../store';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const entityLabels = {
  br_ltd: { name: 'Bonjour Residences Ltd', country: 'UK', system: 'Xero' },
  eci: { name: 'Edimbourg Consulting International', country: 'France', system: 'Pennylane' },
  bi_ltd: { name: 'BI Ltd', country: 'UK', system: 'Xero' },
  fan_zhang: { name: 'Fan Zhang', country: 'UK/France', system: 'Manual' },
  fan_zhang_piotr_kacki: { name: 'Fan Zhang and Piotr Kacki', country: 'UK/France', system: 'Pennylane' },
  piotr_kacki: { name: 'Piotr Kacki', country: 'UK/France', system: 'Manual' },
};

export function SettingsPage() {
  const { user } = useAuthStore();
  const { theme, setTheme } = useThemeStore();
  const { entities } = useEntityStore();
  const [notifications, setNotifications] = useState({
    email: true,
    slack: true,
    whatsapp: false,
    paymentReceived: true,
    paymentDue: true,
    overdueReceivable: true,
    cashflowWarning: true,
  });

  const themeOptions = [
    { value: 'light', label: 'Light', icon: Sun },
    { value: 'dark', label: 'Dark', icon: Moon },
    { value: 'system', label: 'System', icon: Monitor },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
          <p className="text-muted-foreground mt-1">Manage your preferences and configurations</p>
        </div>

        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList>
            <TabsTrigger value="profile">
              <User className="h-4 w-4 mr-2" /> Profile
            </TabsTrigger>
            <TabsTrigger value="appearance">
              <Palette className="h-4 w-4 mr-2" /> Appearance
            </TabsTrigger>
            <TabsTrigger value="notifications">
              <Bell className="h-4 w-4 mr-2" /> Notifications
            </TabsTrigger>
            <TabsTrigger value="entities">
              <Database className="h-4 w-4 mr-2" /> Entities
            </TabsTrigger>
            <TabsTrigger value="integrations">
              <Link className="h-4 w-4 mr-2" /> Integrations
            </TabsTrigger>
          </TabsList>

          {/* Profile */}
          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle>Profile Information</CardTitle>
                <CardDescription>Your account details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Full Name</Label>
                    <Input value={user?.name || ''} disabled />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input value={user?.email || ''} disabled />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Appearance */}
          <TabsContent value="appearance">
            <Card>
              <CardHeader>
                <CardTitle>Appearance</CardTitle>
                <CardDescription>Customize the look and feel</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <Label>Theme</Label>
                  <div className="grid grid-cols-3 gap-4">
                    {themeOptions.map((option) => (
                      <div
                        key={option.value}
                        className={cn(
                          'flex flex-col items-center gap-2 p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md',
                          theme === option.value && 'border-primary bg-primary/5 ring-2 ring-primary'
                        )}
                        onClick={() => setTheme(option.value)}
                        data-testid={`theme-${option.value}`}
                      >
                        <option.icon className="h-8 w-8" />
                        <span className="font-medium">{option.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications */}
          <TabsContent value="notifications">
            <Card>
              <CardHeader>
                <CardTitle>Notification Preferences</CardTitle>
                <CardDescription>Choose how you want to be notified</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h4 className="font-medium mb-4">Channels</h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Email Notifications</p>
                        <p className="text-sm text-muted-foreground">Receive alerts via email</p>
                      </div>
                      <Switch
                        checked={notifications.email}
                        onCheckedChange={(v) => setNotifications({ ...notifications, email: v })}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">Slack Integration</p>
                        <p className="text-sm text-muted-foreground">Post alerts to Slack channel</p>
                      </div>
                      <Switch
                        checked={notifications.slack}
                        onCheckedChange={(v) => setNotifications({ ...notifications, slack: v })}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">WhatsApp Notifications</p>
                        <p className="text-sm text-muted-foreground">Receive critical alerts via WhatsApp</p>
                      </div>
                      <Switch
                        checked={notifications.whatsapp}
                        onCheckedChange={(v) => setNotifications({ ...notifications, whatsapp: v })}
                      />
                    </div>
                  </div>
                </div>
                <Separator />
                <div>
                  <h4 className="font-medium mb-4">Alert Types</h4>
                  <div className="space-y-4">
                    {[
                      { key: 'paymentReceived', label: 'Client Payment Received', desc: 'When a client payment is matched' },
                      { key: 'paymentDue', label: 'Supplier Payment Due', desc: 'When an invoice is due within 3 days' },
                      { key: 'overdueReceivable', label: 'Overdue Receivables', desc: 'When invoices are past due' },
                      { key: 'cashflowWarning', label: 'Cashflow Warning', desc: 'When projected balance drops below threshold' },
                    ].map((item) => (
                      <div key={item.key} className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{item.label}</p>
                          <p className="text-sm text-muted-foreground">{item.desc}</p>
                        </div>
                        <Switch
                          checked={notifications[item.key]}
                          onCheckedChange={(v) => setNotifications({ ...notifications, [item.key]: v })}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Entities */}
          <TabsContent value="entities">
            <Card>
              <CardHeader>
                <CardTitle>Business Entities</CardTitle>
                <CardDescription>Configured entities in the system</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.entries(entityLabels).map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <p className="font-medium">{value.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {value.country} • {value.system}
                        </p>
                      </div>
                      <Button variant="outline" size="sm">Configure</Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Integrations */}
          <TabsContent value="integrations">
            <Card>
              <CardHeader>
                <CardTitle>Integrations</CardTitle>
                <CardDescription>Connected services and APIs (MOCKED for demo)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { name: 'Gmail API', status: 'Mocked', desc: 'Email inbox scanning' },
                    { name: 'Xero', status: 'Mocked', desc: 'UK accounting sync' },
                    { name: 'Pennylane', status: 'Mocked', desc: 'French accounting sync' },
                    { name: 'Revolut Business', status: 'Mocked', desc: 'Bank transactions' },
                    { name: 'Wise', status: 'Mocked', desc: 'Multi-currency transfers' },
                    { name: 'BNP Paribas', status: 'Mocked', desc: 'French banking' },
                    { name: 'Google Drive', status: 'Mocked', desc: 'Document storage' },
                    { name: 'Slack', status: 'Not Connected', desc: 'Notification channel' },
                    { name: 'WhatsApp Business', status: 'Not Connected', desc: 'Alert notifications' },
                  ].map((int) => (
                    <div key={int.name} className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <p className="font-medium">{int.name}</p>
                        <p className="text-sm text-muted-foreground">{int.desc}</p>
                      </div>
                      <Button variant={int.status === 'Mocked' ? 'secondary' : 'outline'} size="sm" disabled>
                        {int.status}
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
